import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import useReveal from '../components/useReveal';
import { useLang } from '../context/LanguageContext';
import t from '../translations';
import './Contact.css';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ fname: '', lname: '', phone: '', email: '', propType: '', message: '' });
  const [services, setServices] = useState([]);

  const sideRef = useReveal();
  const formRef = useReveal();
  const { lang } = useLang();
  const tx = t[lang];

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const toggleService = s => setServices(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const handleSubmit = () => {
    if (!form.fname || !form.phone) {
      setError(true);
      setTimeout(() => setError(false), 2500);
      return;
    }

    const templateParams = {
      from_name: `${form.fname} ${form.lname}`,
      from_email: form.email,
      phone: form.phone,
      prop_type: form.propType,
      services: services.join(', ') || 'Not specified',
      message: form.message || 'No message provided',
    };

    emailjs.send(
      'service_ctiwsda',
      'template_44bdzi7',
      templateParams,
      'f8HpvA2bNNZvX_T0A'
    )
    .then(() => setSubmitted(true))
    .catch(() => {
      alert('Something went wrong. Please call us directly at (728) 226-4437.');
    });
  };

  const SERVICE_OPTIONS = tx.cp_service_options;
  const PROP_TYPES = tx.cp_prop_types;

  return (
    <main>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-inner">
            <div className="breadcrumb">
              <Link to="/">{tx.home}</Link><span className="sep">›</span><span>{tx.contact}</span>
            </div>
            <div className="eyebrow">{tx.cp_eyebrow}</div>
            <h1>{tx.cp_h1_1}<br />{tx.cp_h1_2} <span>{tx.cp_h1_highlight}</span></h1>
            <p>{tx.cp_p}</p>
          </div>
        </div>
        <div className="page-hero-diagonal" />
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-layout">

            {/* Sidebar */}
            <div className="contact-sidebar reveal" ref={sideRef}>
              <span className="tag">{tx.cp_tag}</span>
              <h2>{tx.cp_h2_1} <span>{tx.cp_h2_2}</span></h2>
              <p>{tx.cp_sidebar_p}</p>

              <div className="info-card">
                <div className="info-card-header">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="var(--orange)">
                    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
                  </svg>
                  <span>Phone</span>
                </div>
                <div className="info-card-body">
                  <a href="tel:7282264437">(728) 226-4437</a>
                  <p className="sub-text">Call or text anytime during business hours</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-header">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="var(--orange)">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span>Email</span>
                </div>
                <div className="info-card-body">
                  <a href="mailto:xscinvest@gmail.com">xscinvest@gmail.com</a>
                  <p className="sub-text">We reply within 24 hours</p>
                </div>
              </div>

              <div className="hours-block">
                <h4>{tx.cp_hours}</h4>
                <div className="hours-row"><span className="day">{tx.cp_days}</span><span className="time">7:00 AM – 7:00 PM</span></div>
              </div>

              <div className="lang-strip">
                <span className="lang-pill lp-en">🇺🇸 English</span>
                <span className="lang-pill lp-es">🇪🇸 Español</span>
              </div>
            </div>

            {/* Form */}
            <div className="contact-form-wrap reveal" ref={formRef} style={{ transitionDelay: '0.15s' }}>
              {!submitted ? (
                <>
                  <h3>{tx.cp_form_h3}</h3>
                  <p className="form-sub">{tx.cp_form_sub}</p>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{tx.cp_fname}</label>
                      <input name="fname" value={form.fname} onChange={handleChange} placeholder="John" />
                    </div>
                    <div className="form-group">
                      <label>{tx.cp_lname}</label>
                      <input name="lname" value={form.lname} onChange={handleChange} placeholder="Smith" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{tx.cp_phone}</label>
                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="(555) 000-0000" type="tel" />
                  </div>
                  <div className="form-group">
                    <label>{tx.cp_email}</label>
                    <input name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" type="email" />
                  </div>
                  <div className="form-group">
                    <label>{tx.cp_prop}</label>
                    <select name="propType" value={form.propType} onChange={handleChange}>
                      <option value="">{tx.cp_prop_placeholder}</option>
                      {PROP_TYPES.map(pt => <option key={pt}>{pt}</option>)}
                    </select>
                  </div>

                  <span className="form-label-lg">{tx.cp_services_label}</span>
                  <div className="form-checkboxes">
                    {SERVICE_OPTIONS.map(s => (
                      <label className="form-check" key={s}>
                        <input type="checkbox" checked={services.includes(s)} onChange={() => toggleService(s)} />
                        <span>{s}</span>
                      </label>
                    ))}
                  </div>

                  <div className="form-group">
                    <label>{tx.cp_message}</label>
                    <textarea name="message" value={form.message} onChange={handleChange} placeholder={tx.cp_message_placeholder} />
                  </div>

                  <button className={`form-submit${error ? ' error' : ''}`} onClick={handleSubmit}>
                    {error ? tx.cp_required : (
                      <>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                        {tx.cp_submit}
                      </>
                    )}
                  </button>
                  <p className="form-note">{tx.cp_note}</p>
                </>
              ) : (
                <div className="success-msg">
                  <div className="check">
                    <svg viewBox="0 0 24 24" width="32" height="32" fill="#fff"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  </div>
                  <h3>{tx.cp_success_h3}</h3>
                  <p>{tx.cp_success_p}<br /><br />{tx.cp_success_p2} <strong>(728) 226-4437</strong>.</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>
    </main>
  );
}
